// Use this for operations that will affect multiple known, or possibly all, files
// The first three arguments are the title and messages to show in the confirmation dialog
// The fourth argument are the ID of the affected files, or null if all files are possibly affected
// The operation itself (last argument) will get a callback to call after the operation is done,
//  which will make sure the frontend is updated.
//  The callback also takes affected file ids or null for all files as an argument
import React, { Fragment, useCallback, useContext } from 'react';
import MarkdownHtml from 'studio/components/MarkdownHtml/MarkdownHtml';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { EditorContext } from 'studio/pages/Editor/Editor';
import styles from 'studio/pages/Editor/Editor.styles';
import MuiLink from '@mui/material/Link';
import makeStyles from '@mui/styles/makeStyles';

export const getDistinguishableName = (path, paths) => {
  const elements = path.split('/').reverse();
  // the maximum number of path elements in common with another open file
  let n = 0;
  paths.forEach(path2 => {
    // don't compare the file to itself
    if (path2 !== path) {
      const elements2 = path2.split('/').reverse();
      let i = 0;
      // compute the number of path matching elements
      for (; elements[i] && elements2[i] && elements[i] === elements2[i]; i++);
      // set new maximum
      n = Math.max(i, n);
    }
  });
  return elements
    .slice(0, n + 1)
    .reverse()
    .join('/');
};

// replaces the whole text of the specified model with the specified text
export const replaceText = (model, text, range) => {
  const editOperation = {
    range: range || model.getFullModelRange(),
    text
  };
  model.pushEditOperations([], [editOperation], () => []);
};

export const CODE_PASSAGE_TYPE_STRING = 'CODE';
export const LINK_PASSAGE_TYPE_STRING = 'LINK';
export const TEXT_PASSAGE_TYPE_STRING = 'TEXT';
export const DELETE_PASSAGE_TYPE_STRING = 'DELETE';
export const SYNC_PASSAGE_TYPE_STRING = 'SYNC';
export const CONCEPT_PASSAGE_TYPE_STRING = 'CONCEPT';

// Note: this function is curried to inject its dependencies
export const useCreateKgMessage = (classes, setFilesToDelete, openSyncDialog, open, getFiles, activeFile) =>
  // message: the knowledge graph message to create react elements for
  // callback: called after a click on a passage happens, it is passed the clicked passage
  // returns: the react element passages and a boolean indicating if the message contains a user prompt
  useCallback(
    (message, callback = () => {}) => {
      let isUserPrompt = false;

      const passages = message.passages.map((passage, idx) => {
        if (passage.type === CODE_PASSAGE_TYPE_STRING) {
          return <code key={idx}>{passage.code}</code>;
        } else if (passage.type === LINK_PASSAGE_TYPE_STRING) {
          return (
            <MuiLink
              key={idx}
              color="primary"
              className={classes.actionLink}
              target="_blank"
              rel="noopener noreferrer"
              href={passage.link}
            >
              {passage.text}
            </MuiLink>
          );
        } else if (passage.type !== TEXT_PASSAGE_TYPE_STRING) {
          let onClick;
          let content = passage.text;
          if (passage.type === DELETE_PASSAGE_TYPE_STRING) {
            isUserPrompt = true;
            onClick = async () => {
              const files = await getFiles();
              const filesToDelete = passage.fileDeletionPaths.map(path => files[path]);
              setFilesToDelete(filesToDelete);
            };
          } else if (passage.type === SYNC_PASSAGE_TYPE_STRING) {
            isUserPrompt = true;
            onClick = () => openSyncDialog(activeFile.data.id, passage.classesToSync);
          } else if (passage.type === CONCEPT_PASSAGE_TYPE_STRING) {
            onClick = () => open(passage.filePath, passage.text); // text is the qualified VKL identifier here
            // display the identifier as a code tag
            content = <code key={idx}>{passage.text}</code>;
            // Passages of "broken" concepts cannot be jumped to (broken concepts are those that don't actually exist in the KG)
            if (passage.isBroken) {
              return content;
            }
          }
          return (
            <MuiLink
              key={idx}
              color="primary"
              className={classes.actionLink}
              onClick={e => {
                e.preventDefault();
                onClick();
                callback(passage);
              }}
              data-test="kgMessageActionLink"
            >
              {content}
            </MuiLink>
          );
        } else {
          // the tags make sure there is enough space around the text node (even though no whitespace gets added)
          return <Fragment key={idx}> {passage.text} </Fragment>;
        }
      });

      return [passages, isUserPrompt];
    },
    [classes, setFilesToDelete, openSyncDialog, open, activeFile]
  );

export const useEditorUtils = () => {
  const { showConfirmationModal } = useConfirmationModal();
  const classes = makeStyles(styles);

  const {
    fileState: { activeFilePath, openFilePaths, fileContents, files, activeFile },
    fileOperations: { getFiles, closeFiles, reloadFileContents },
    refs: { conceptTreeRef },
    deletion: { setFilesToDelete },
    sync: { openSyncDialog },
    navigation: { openInEditor }
  } = useContext(EditorContext);

  const operationAffectingMultipleFiles = useCallback(
    (title, operationMessage1, operationMessage2, affectedFileIds, op) => {
      // returns the paths of the open files that are affected given the specified affected file IDs
      const openAffectedFilePaths = affectedFileIds =>
        openFilePaths.filter(
          openFilePath =>
            affectedFileIds === null ||
            affectedFileIds.some(affectedFileId => affectedFileId === files[openFilePath].data.id)
        );
      // the paths of the modified files affected by the operation
      const modifiedAffectedFilePaths = openAffectedFilePaths(affectedFileIds).filter(
        path => fileContents[path].isModified
      );
      // show a dialog if there is an affected file which is modified
      if (modifiedAffectedFilePaths.length > 0) {
        showConfirmationModal(() => ({
          title,
          content: (
            <>
              <MarkdownHtml html={`${operationMessage1} affects the following files with unsaved changes:`} />
              <ul className={classes.modalFilePathListElement}>
                {modifiedAffectedFilePaths.map(path => (
                  <li key={path}>{path}</li>
                ))}
              </ul>
              Please save or discard these changes before {operationMessage2}.
            </>
          ),
          // the text for the confirm button (only shown if there is an 'onConfirm')
          confirmButtonText: 'Show file',
          // opens one of the modified affected files (only if the currently active file is not part of them)
          onConfirm:
            !modifiedAffectedFilePaths.includes(activeFilePath) && (() => openInEditor(modifiedAffectedFilePaths[0])),
          cancelDataTest: 'modifiedFilesModificationDialogClose'
        }));
      } else {
        const reloadFrontend = async affectedFileIds => {
          const newFiles = await getFiles();
          // the paths of the open files that have been deleted and need to be closed
          const filePathsToClose = openFilePaths.filter(openFilePath => !newFiles[openFilePath]);
          // the paths of the open files that have been affected and need to be reloaded
          const filePathsToReload = openAffectedFilePaths(affectedFileIds).filter(
            path => !filePathsToClose.includes(path)
          );
          // close the open files that have been deleted
          closeFiles(...filePathsToClose);
          // reload the open files that have been modified
          reloadFileContents(...filePathsToReload);
          conceptTreeRef.current.reloadTreeItems();
        };
        // execute the callback for the operation, passing it the 'op' callback to execute subsequently
        op(reloadFrontend);
      }
    },
    [openFilePaths, files, fileContents, activeFilePath, openInEditor, getFiles, closeFiles, reloadFileContents]
  );

  const createKgMessage = useCreateKgMessage(
    classes,
    setFilesToDelete,
    openSyncDialog,
    files,
    openInEditor,
    activeFile
  );

  return {
    operationAffectingMultipleFiles,
    createKgMessage
  };
};
